import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Button } from "react-bootstrap";
import FormHandler from "../../Common/Form/FormHandler/FormHandler";
import { schema, createRegionFormConfig } from "../../../../JsonData/CreateRegionConfig";
import AddDeleteTableRows from "../AddDeleteTableRows/AddDeleteTableRows";
import { postDataWithBody, putDataWithBody } from "../../../../Services/Services";
import { toast } from "react-toastify";
import "./CreateRegion.css";
import {
    createRegion, updateRegion, findRegionById
} from "../../../../Services/EndPoints";
import { yupResolver } from "@hookform/resolvers/yup";

// component for creating and editing regions in a hierarchical structure
const RegionRegistrationForm = (props) => {
    const [regionConfig, setRegionConfig] = useState(createRegionFormConfig);
    const [regionTypeLabel, setRegionTypeLabel] = useState("Name"); // Default label
    const [regionInfoLabel, setRegionInfoLabel] = useState("Region Information"); // Default info label
    const [showZipcode, setShowZipcode] = useState(true); // Control zipcode field visibility
    const [disableZipcode, setDisableZipcode] = useState(false); // Control zipcode field disabled state

    const [rowValues, setRowValues] = useState([]);
    const [dNoneClass, setDNoneClass] = useState("d-none");
    const [activeTab, setActiveTab] = useState(0);

    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        trigger,
        reset,
        control,
        formState: { errors },
    } = useForm({ resolver: yupResolver(schema[activeTab]), mode: "all", context: { disableZipcode } });

    // Generates form fields using FormHandler component
    const GenerateForm = (fields, register, errors, control) => {
        return (
            <FormHandler
                register={register}
                errors={errors}
                fields={fields}
                control={control}
                columns={1}
                getValues={getValues}
            />
        );
    };

    // Processes table data and generates form components for each tab
    const generateTabsData = (tabWiseData) => {
        tabWiseData?.forEach((element) => {
            return (element.component = GenerateForm(
                element?.fields,
                register,
                errors,
                control
            ));
        });
        return tabWiseData;
    };

    // Handles form submission for both create and edit operations
    const submitHandler = async (data, e) => {
        let res = null;
        e.preventDefault();
        data["attribute"] = rowValues;
        data["status"] = true;

        // Determine the type based on the keyVal structure
        const keyParts = props.keyVal?.split('-') || [];
        const level = keyParts.length;

        if (level === 2) {
            // District level
            data["type"] = "DISTRICT";
        } else if (level === 3) {
            // Block level
            data["type"] = "BLOCK";
        }

        if (props.id != null && props.actionType === "EDIT") {
            data["id"] = props.id;
            res = await putDataWithBody(updateRegion, data, {});
            if (res.status === "success") {
                window.regionTreeComponent.hideModalPopup();
                toast.success(res.msg, {
                    position: toast.POSITION.TOP_CENTER,
                    autoClose: 2000, // Close after 2 seconds
                });

            }
            else {
                toast.error(res.msg, {
                    position: toast.POSITION.TOP_CENTER,
                    autoClose: 2000, // Close after 2 seconds
                });
            }

        }
        else {
            let hierListKeys = ["hier_level_one_id", "hier_level_two_id", "hier_level_three_id", "hier_level_four_id", "hier_level_five_id"]
            let hierIdList = props.keyVal.split("-");

            hierIdList.forEach(function (hier, index) {
                data[hierListKeys[index]] = hier;
            });
            data["parent_region_id"] = hierIdList[hierIdList.length - 1];
            data["region_type_id"] = props.regionTypeId;

            res = await postDataWithBody(createRegion, data, {});
            if (res.status === "success") {
                window.regionTreeComponent.hideModalPopup();
                toast.success(res.msg, {
                    position: toast.POSITION.TOP_CENTER,
                    autoClose: 2000, // Close after 2 seconds
                });
            }
            else {
                toast.error(res.msg, {
                    position: toast.POSITION.TOP_CENTER,
                    autoClose: 2000, // Close after 2 seconds
                });
            }
        }
    };

    // Updates row values for dynamic table
    const updateRowValues = (newRowValues) => {
        setRowValues(newRowValues);
    };

    // update labels and field behavior based on hierarchy level
    useEffect(() => {
        if (props.keyVal) {
            const keyParts = props.keyVal.split('-');
            const level = keyParts.length;

            // Update labels and field behavior based on hierarchy level
            if (level === 2) {
                // District level
                setRegionTypeLabel("District Name");
                setRegionInfoLabel("District Information");
                setShowZipcode(true); // Show zipcode for districts

                // Check if in edit mode for district
                if (props.actionType === "EDIT") {
                    setDisableZipcode(true); // Disable zipcode for district editing
                } else {
                    setDisableZipcode(false);
                }
            }
            else if (level === 3) {
                // Block level
                setRegionTypeLabel("Block Name");
                setRegionInfoLabel("Block Information");
                setShowZipcode(false); // Hide zipcode for blocks
                setDisableZipcode(false);
            }
            else {
                // Default/other levels
                setRegionTypeLabel("Name");
                setRegionInfoLabel("Region Information");
                setShowZipcode(true);
                setDisableZipcode(false);
            }
        }
    }, [props.keyVal, props.actionType]);

    // update form configuration when labels or field behavior changes
    useEffect(() => {
        if (regionConfig.length > 0) {
            const updatedConfig = [...regionConfig];

            // Update name field label
            const nameFieldIndex = updatedConfig[0].fields.findIndex(field => field.name === "name");
            if (nameFieldIndex !== -1) {
                updatedConfig[0].fields[nameFieldIndex].label = regionTypeLabel;
            }

            // Update zipcode field behavior
            const zipcodeFieldIndex = updatedConfig[0].fields.findIndex(field => field.name === "zipcode");
            if (zipcodeFieldIndex !== -1) {
                updatedConfig[0].fields[zipcodeFieldIndex].hidden = !showZipcode;
                updatedConfig[0].fields[zipcodeFieldIndex].readOnly = disableZipcode;
                updatedConfig[0].fields[zipcodeFieldIndex].disabled = disableZipcode;

                // Update validation requirements based on disabled state
                updatedConfig[0].fields[zipcodeFieldIndex].mandatory = !disableZipcode;
            }

            setRegionConfig(updatedConfig);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [regionTypeLabel, showZipcode, disableZipcode]);

    // show/hide region parameters based on hierarchy level
    useEffect(() => {
        if (props.keyVal.split("-").length === 5) {
            setDNoneClass("");
        }
        else {
            setDNoneClass("d-none");
        }

    }, [props.keyVal]);

    // populate form in edit mode or reset for create mode
    useEffect(() => {
        if (props.actionType != null && props.actionType === "EDIT") {
            populateRegionFormFrEdit(props.id, props.key);
        }
        else {
            reset();
            setRowValues([{ "attributeName": "Total Male", "attributeValue": "" }]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.updateRegionData]);

    // Populates form with existing data when in edit mode
    const populateRegionFormFrEdit = async (id, key) => {
        var regionData = await postDataWithBody(findRegionById, [id], {});
        regionData = regionData[0];
        setValue("name", regionData.name);
        setValue("description", regionData.description);
        setValue("zipcode", regionData.zipcode);
        setRowValues(regionData.attribute);

        // If editing a district, ensure zipcode is disabled
        if (props.keyVal && props.keyVal.split('-').length === 2) {
            setDisableZipcode(true);
        } else {
            setDisableZipcode(false);
        }
    };

    // Handles saving current tab data and moving to next tab
    const saveTabDataHandler = async (e) => {
        const isStepValid = await trigger();
        if (isStepValid) setActiveTab((prevId) => prevId + 1);
    };

    // Handles moving to previous tab
    const previousHandler = () => {
        if (activeTab > 0) {
            setActiveTab((prevId) => prevId - 1);
        }
    };

    const tabsData = generateTabsData(regionConfig);

    return (
        <div className="">
            <div className="form-body">
                <div className="registration__form--body">
                    <form onSubmit={handleSubmit(submitHandler)} name="AgancyRegForm">
                        {/* Updated label to use dynamic regionInfoLabel */}
                        <div class="label_text container pb-2 ps-3 mb-4">{regionInfoLabel}</div>

                        <div>
                            {tabsData?.map((tab) => (
                                <>
                                    {tab.id === activeTab && (
                                        <>
                                            <div className="registration__form--heading">
                                                {tab.heading}
                                            </div>
                                            <div>{tab.component}</div>
                                        </>
                                    )}
                                </>
                            ))}
                        </div>
                        <div className={dNoneClass}>
                            <div class="label_text container pb-2 ps-3 mt-3 mb-4">Region Parameters</div>
                            <div>
                                {" "}
                                <AddDeleteTableRows
                                    rowValues={rowValues}
                                    updateRowValues={updateRowValues}
                                />
                            </div>
                        </div>
                        <div className="d-flex justify-content-center">
                            {activeTab > 0 && (
                                <Button
                                    variant="secondary"
                                    className="previousbutton-style mx-2"
                                    onClick={previousHandler}
                                >
                                    Previous
                                </Button>
                            )}
                            {tabsData.length - 1 === activeTab && (
                                <Button type="submit" className="mx-2" variant="primary">
                                    Save & Submit
                                </Button>
                            )}
                            {tabsData.length - 1 !== activeTab && (
                                <Button
                                    variant="primary"
                                    className="mx-2"
                                    onClick={saveTabDataHandler}
                                >
                                    Save & Next
                                </Button>
                            )}
                        </div>

                    </form>
                </div>
            </div>
        </div>
    );
};
export default RegionRegistrationForm;